import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { safeHref } from "@/lib/format";
import { PriceTag } from "@/components/site/ServiceCard";
import { Accent } from "@/components/site/Primitives";

const TICKER = ["Reels", "Shorts", "Podcast clips", "Talking head", "Founder content", "Motion graphics", "Brand films", "Fashion & editorial"];

export const Ticker = () => (
  <div className="relative border-y border-[#27272A] bg-[#0055FF] overflow-hidden py-3" data-testid="format-ticker" aria-label={TICKER.join(", ")}>
      <div className="flex w-max eb-marquee" style={{ animationDuration: "40s" }} aria-hidden="true">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="px-6 font-display text-lg md:text-xl font-semibold tracking-[-0.02em] text-white whitespace-nowrap">
              {i % 3 === 1 ? <span className="eb-accent !text-black">{t}</span> : t}<span className="ml-12 text-black/60">{"\u2726"}</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const CtaLink = ({ cta, primary, testid }) => {
  if (!cta?.label) return null;
  const href = safeHref(cta.href);
  const cls = primary ? "eb-btn-primary" : "eb-btn-ghost";
  return href.startsWith("/") ? (
    <Link to={href} className={cls} data-testid={testid}>{cta.label}{primary && <ArrowRight size={16} />}</Link>
  ) : (
    <a href={href} className={cls} data-testid={testid} target="_blank" rel="noopener noreferrer">{cta.label}</a>
  );
};

export const Hero = ({ site, services }) => {
  const h = site.hero || {};
  const simple = services.find((s) => s.category === "Simple" && !s.custom_quote && s.price_inr != null);
  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="section-hero">
      {h.image_url && <img src={h.image_url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      <div className="absolute inset-0 eb-grain opacity-[0.07] mix-blend-overlay pointer-events-none" />
      <div className="relative eb-container pb-16 md:pb-24 pt-32 grid lg:grid-cols-12 gap-12 items-end">
        <motion.div className="lg:col-span-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
          {h.eyebrow && <p className="eb-label !text-[#00E5FF] mb-6" data-testid="hero-eyebrow">{h.eyebrow}</p>}
          <h1 className="text-5xl sm:text-6xl lg:text-[7.5rem] font-semibold tracking-[-0.045em] leading-[0.92]" data-testid="hero-headline"><Accent text={h.headline} /></h1>
          {h.subheadline && <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed" data-testid="hero-subheadline">{h.subheadline}</p>}
          <div className="mt-10 flex flex-wrap gap-4">
            <CtaLink cta={h.primary_cta} primary testid="hero-primary-cta" />
            <CtaLink cta={h.secondary_cta} testid="hero-secondary-cta" />
          </div>
        </motion.div>
        <motion.div className="lg:col-span-4 space-y-px" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.6 }}>
          {simple && (
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-6" data-testid="hero-price-readout">
              <p className="eb-label mb-2">{simple.name}</p>
              <PriceTag service={simple} />
            </div>
          )}
          {site.turnaround?.headline && (
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-6 flex gap-4" data-testid="hero-turnaround">
              <Clock className="text-[#0055FF] shrink-0" size={22} />
              <p className="text-sm text-zinc-300">{site.turnaround.headline}</p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export { CtaLink };
